import React, { useEffect, useRef } from 'react';
import { transform } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OlFeature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import { Style, Stroke } from 'ol/style';
import { CrossSectionPanel } from '@pages/mapvisualization/components/clipping/cross-section-panel';
import { demStore } from '@shared/reality-capture/dem-store';

const CROSS_SECTION_LINE_LAYER_ID = 'volume-cross-section-line';

// Feature property keys set by generateAlignmentArea() for alignment-calculation mode.
const ALIGNMENT_EN_POINTS_KEY = 'alignmentEnPoints';
const ALIGNMENT_CORRIDOR_WIDTH_KEY = 'alignmentCorridorWidth';
const ALIGNMENT_SIDE_KEY = 'alignmentSide';

// Centerline points and the corridor width are stored in EPSG:3857 (the map projection),
// because latLonToEN() projects to Web Mercator. Keep all perpendicular maths in that CRS.
const ALIGNMENT_CRS = 'EPSG:3857';

type CrossSectionGeometry = {
    // Endpoints in the map projection, used to draw the red line.
    p1: number[];
    p2: number[];
    // Endpoints as lat/lon, used as the cross-section request coordinates.
    coords: { lat1: number; lon1: number; lat2: number; lon2: number };
};

type BaseCoords = {
    minLon: number; maxLon: number; centerLon: number;
    minLat: number; maxLat: number;
};

const calcBaseCoords = (measurement: any, toolsStore: any): BaseCoords | null => {
    if (!measurement?.feature?.getGeometry) return null;
    try {
        const map = toolsStore.mapWrapper.getInstance();
        const mapProj = map.getView().getProjection();
        const extent = measurement.feature.getGeometry().getExtent();
        const sw = transform([extent[0], extent[1]], mapProj, 'EPSG:4326');
        const ne = transform([extent[2], extent[3]], mapProj, 'EPSG:4326');
        return {
            minLon: sw[0], maxLon: ne[0],
            centerLon: (sw[0] + ne[0]) / 2,
            minLat: sw[1], maxLat: ne[1],
        };
    } catch (e) {
        console.error('Error calculating cross section coords:', e);
        return null;
    }
};

const buildSurfaceSelection = (measurement: any, toolsStore: any) => {
    const includeDemUuids: string[] = [];
    const includeModelIds: number[] = [];
    const layers = toolsStore?.mapWrapper?.tileLayers?.layersDefinitions;

    for (const surface of [measurement?.calcReference, measurement?.calcTarget]) {
        if (!surface) continue;
        if (surface.surfaceType === 'dem' && surface.surfaceDemUrl) {
            const layer = layers?.find((l: any) => l.demUrl === surface.surfaceDemUrl);
            if (layer?.fileUuid) includeDemUuids.push(layer.fileUuid);
        } else if (surface.surfaceType === 'model' && surface.surfaceModelUuid) {
            const id = demStore.getModelIdByUuid(surface.surfaceModelUuid);
            if (id != null) includeModelIds.push(id);
        }
    }

    return { includeDemUuids, includeModelIds };
};

// In alignment-calculation mode the cut must be perpendicular to the alignment, not axis-aligned.
// Returns null when the measurement is not an alignment corridor so the caller can fall back.
const buildAlignmentCrossSection = (
    measurement: any,
    map: any,
    base: BaseCoords | null,
    lonOffset: number,
): CrossSectionGeometry | null => {
    const enPoints: number[][] = measurement?.feature?.get?.(ALIGNMENT_EN_POINTS_KEY);
    const corridorWidth: number = measurement?.feature?.get?.(ALIGNMENT_CORRIDOR_WIDTH_KEY);
    if (!Array.isArray(enPoints) || enPoints.length < 2 || !corridorWidth) return null;

    // Cumulative arc length along the centerline (EPSG:3857 units).
    const cumulative: number[] = [0];
    for (let i = 1; i < enPoints.length; i++) {
        const prev = enPoints[i - 1];
        const curr = enPoints[i];
        cumulative.push(cumulative[i - 1] + Math.hypot(curr[0] - prev[0], curr[1] - prev[1]));
    }
    const totalLength = cumulative[cumulative.length - 1];
    if (totalLength <= 0) return null;

    // Reuse the W/E offset as a position along the alignment: 0 = start, 0.5 = middle, 1 = end.
    const fraction = base && base.maxLon > base.minLon
        ? (Math.max(base.minLon, Math.min(base.maxLon, base.centerLon + lonOffset)) - base.minLon) /
          (base.maxLon - base.minLon)
        : 0.5;
    const targetDist = Math.max(0, Math.min(1, fraction)) * totalLength;

    // Locate the centerline segment containing targetDist and interpolate the cut point.
    let seg = 0;
    while (seg < cumulative.length - 2 && cumulative[seg + 1] < targetDist) seg++;
    const segStart = enPoints[seg];
    const segEnd = enPoints[seg + 1];
    const segLength = cumulative[seg + 1] - cumulative[seg] || 1;
    const t = Math.max(0, Math.min(1, (targetDist - cumulative[seg]) / segLength));
    const centerX = segStart[0] + t * (segEnd[0] - segStart[0]);
    const centerY = segStart[1] + t * (segEnd[1] - segStart[1]);

    // Unit tangent of the segment, then its perpendicular (normal).
    let tangentX = segEnd[0] - segStart[0];
    let tangentY = segEnd[1] - segStart[1];
    const tangentLength = Math.hypot(tangentX, tangentY) || 1;
    tangentX /= tangentLength;
    tangentY /= tangentLength;
    const normalX = -tangentY;
    const normalY = tangentX;

    // Span only the selected side, matching the corridor polygon's edges. "Left" is
    // the +normal half, "right" the -normal half; a one-sided cut runs centerline → edge.
    const side = measurement?.feature?.get?.(ALIGNMENT_SIDE_KEY) ?? 'both';
    const half = corridorWidth / 2;
    const center = [centerX, centerY];
    const leftEdge = [centerX + normalX * half, centerY + normalY * half];
    const rightEdge = [centerX - normalX * half, centerY - normalY * half];

    const endA = side === 'right' ? center : leftEdge;
    const endB = side === 'left' ? center : rightEdge;

    const mapProj = map.getView().getProjection();
    const ll1 = transform(endA, ALIGNMENT_CRS, 'EPSG:4326');
    const ll2 = transform(endB, ALIGNMENT_CRS, 'EPSG:4326');

    return {
        p1: transform(endA, ALIGNMENT_CRS, mapProj),
        p2: transform(endB, ALIGNMENT_CRS, mapProj),
        coords: { lon1: ll1[0], lat1: ll1[1], lon2: ll2[0], lat2: ll2[1] },
    };
};

// Resolves the cross-section line geometry: perpendicular to the alignment when available,
// otherwise the legacy north-south line swept east-west by lonOffset.
const buildCrossSectionGeometry = (
    measurement: any,
    toolsStore: any,
    lonOffset: number,
): CrossSectionGeometry | null => {
    const map = toolsStore?.mapWrapper?.getInstance?.();
    if (!map) return null;

    const base = calcBaseCoords(measurement, toolsStore);

    const aligned = buildAlignmentCrossSection(measurement, map, base, lonOffset);
    if (aligned) return aligned;

    if (!base) return null;

    const mapProj = map.getView().getProjection();
    const adjustedLon = Math.max(base.minLon, Math.min(base.maxLon, base.centerLon + lonOffset));
    return {
        p1: transform([adjustedLon, base.minLat], 'EPSG:4326', mapProj),
        p2: transform([adjustedLon, base.maxLat], 'EPSG:4326', mapProj),
        coords: { lat1: base.minLat, lon1: adjustedLon, lat2: base.maxLat, lon2: adjustedLon },
    };
};

export const VolumeCrossSection = ({ measurement, toolsStore, lonOffset, onStoreReady }: { measurement: any, toolsStore: any, lonOffset: number, onStoreReady?: (store: any) => void }) => {
    const lineLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

    const { includeDemUuids, includeModelIds } = buildSurfaceSelection(measurement, toolsStore);

    useEffect(() => {
        const removeLayer = (): void => {
            if (!lineLayerRef.current) return;
            try {
                toolsStore?.mapWrapper?.getInstance?.()?.removeLayer(lineLayerRef.current);
            } catch (_) { /* ignore */ }
            lineLayerRef.current = null;
        };

        const map = toolsStore?.mapWrapper?.getInstance?.();
        const geometry = buildCrossSectionGeometry(measurement, toolsStore, lonOffset);

        if (!geometry || !map) {
            removeLayer();
            return removeLayer;
        }

        try {
            const lineFeature = new OlFeature({
                geometry: new LineString([geometry.p1, geometry.p2]),
            });
            const source = new VectorSource({ features: [lineFeature] });
            const layer = new VectorLayer({
                source,
                style: new Style({ stroke: new Stroke({ color: '#ff0000', width: 2 }) }),
                zIndex: 10000,
                properties: { id: CROSS_SECTION_LINE_LAYER_ID },
            });
            map.addLayer(layer);
            lineLayerRef.current = layer;
        } catch (e) {
            console.error('Error drawing cross section line:', e);
        }

        return removeLayer;
    }, [measurement, toolsStore, lonOffset]);

    const geometry = buildCrossSectionGeometry(measurement, toolsStore, lonOffset);
    const coords = geometry?.coords ?? null;

    return (
        <div className="w-full h-full relative z-[9999] bg-white pt-10">
            {coords ? (
                <CrossSectionPanel
                    embedded
                    station={null}
                    lat1={coords.lat1}
                    lon1={coords.lon1}
                    lat2={coords.lat2}
                    lon2={coords.lon2}
                    view={null}
                    searchDisplacement={null}
                    deltaZFilter={null}
                    onStoreReady={onStoreReady}
                    selection={{
                        includeModelIds: includeModelIds,
                        // The volume tool's selection is exhaustive: show only the
                        // chosen surface(s), never every model in the project.
                        strictModelSelection: true,
                        includeLumiDbUuids: [],
                        includeDemUuids: includeDemUuids,
                        alignmentId: null,
                        alignmentIndex: null,
                    }}
                />
            ) : (
                <div className="p-4">Cross section not available</div>
            )}
        </div>
    );
}
